import React, { useState, useEffect, useRef, useCallback } from 'react';
import BrowserWindow from 'components/atoms/BrowserWindow';
// Reuse the shared terminal styling (read-only import — keeps the look identical
// to the original WorkflowDemo without touching that shared component).
import 'components/shared/GetStartedSteps/TestRuns.css';

/**
 * LoginHeroDemo — login-page-only typewriter terminal. A DUPLICATE fork of the
 * shared GetStartedSteps WorkflowDemo so the login hero can show a SINGLE line
 * (just the command, no output) that rotates through a few CLI demos — without
 * touching the shared DemoShowcase used elsewhere. Styles reused via gss-*;
 * font size + height tweaks live in ConnectPage.css (.login-visual-overlay).
 *
 * Edit DEMOS to change the commands.
 */
// Onboarding lifecycle order: install → init → build → deploy → run → connect.
const DEMOS = [
  'npm install -g @zibby/cli',
  'zibby init',
  'zibby agent new my-agent',
  'zibby agent deploy my-agent',
  'zibby agent trigger 8a3f9e2c',
  'zibby mcp install --agent claude-code',
];

const CHAR_SPEED = 16;
const DELETE_SPEED = 9; // fast backspace before rotating
// Target ~4s on screen per command: typing (~0.5s) + this hold ≈ 4s.
const HOLD = 3000;

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const LoginHeroDemo = () => {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState('');
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const cancelRef = useRef(false);
  const timeoutsRef = useRef([]);
  // True while the user hovers the terminal — pauses rotation so they can read /
  // copy the current command.
  const hoverRef = useRef(false);
  const copiedTimerRef = useRef(null);

  const clearAllTimeouts = useCallback(() => {
    cancelRef.current = true;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const delay = useCallback((ms) => {
    return new Promise((resolve) => {
      const t = setTimeout(() => {
        if (!cancelRef.current) resolve();
      }, ms);
      timeoutsRef.current.push(t);
    });
  }, []);

  useEffect(() => {
    cancelRef.current = false;
    setText('');
    setDone(false);
    const command = DEMOS[idx];

    const animate = async () => {
      await delay(380);
      for (let ci = 1; ci <= command.length; ci++) {
        if (cancelRef.current) return;
        setText(command.slice(0, ci));
        await delay(CHAR_SPEED);
      }
      if (cancelRef.current) return;
      setDone(true);
      await delay(HOLD);

      // Pause rotation while the user is hovering (they may want to copy it).
      while (hoverRef.current && !cancelRef.current) {
        await delay(200);
      }
      if (cancelRef.current) return;

      // Fast backspace the whole command before rotating to the next.
      setDone(false);
      for (let ci = command.length - 1; ci >= 0; ci--) {
        if (cancelRef.current) return;
        setText(command.slice(0, ci));
        await delay(DELETE_SPEED);
      }
      if (!cancelRef.current) setIdx((i) => (i + 1) % DEMOS.length);
    };

    animate();
    return clearAllTimeouts;
  }, [idx, delay, clearAllTimeouts]);

  const command = DEMOS[idx];

  const copyCommand = useCallback(() => {
    navigator.clipboard?.writeText(command);
    setCopied(true);
    clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 1300);
  }, [command]);

  // Clean up the copied-flag timer on unmount.
  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  return (
    <div
      className="gss-wrapper"
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; }}
    >
      <BrowserWindow variant="dark" className="gss-terminal">
        <div className="gss-terminal-body">
          <div className="gss-step">
            {/* Whole command line is the click target → click anywhere to copy. */}
            <div
              className="gss-command login-cmd-clickable"
              onClick={copyCommand}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyCommand(); } }}
              title={copied ? 'Copied!' : 'Click to copy'}
            >
              <span className="gss-prompt">$</span>
              <span className="gss-command-text">
                {text}
                <span className="gss-cursor">|</span>
              </span>
              <button className={`gss-copy-btn ${copied ? 'login-copied' : ''}`} onClick={(e) => { e.stopPropagation(); copyCommand(); }} aria-label="Copy command">
                {copied ? <CheckIcon /> : <CopyIcon />}
              </button>
            </div>
          </div>
        </div>
      </BrowserWindow>
    </div>
  );
};

export default LoginHeroDemo;
